import React, { useContext, useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';

import { AuthContext, type CompanyContextType } from '../../../contexts/auth';
import { getCompany } from '../../../api/firebase/firestore/company/read';
import { AuthRegisterSchema, CompanyInviteRegisterSchema } from '../../../validation/users';
import {
  gray100,
  orange50,
  primaryButtonBackgroundColor,
  red50,
  blue20,
  blue50,
} from '../../../constants/colors';
import { CORPORATE_TERMS_URL, CORPORATE_PRIVACY_URL } from '../../../constants/externalUrls';
import { textMb, titleMb, w100 } from '../../../constants/location';
import { isBlank } from '../../../utils/formValue';

import CustomTextfield from '../form/CustomTextfield';
import CustomLoading from '../form/CustomLoading';
import CustomSnackbar from '../form/CustomSnackbar';
import PasswordTextfield from '../form/PasswordTextfield';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../../api/firebase/config';
import {
  validateReferralToken,
  redeemReferralInvitation,
} from '../../../api/firebase/function/referralFunction';
import { validateCouponCode, redeemCoupon } from '../../../api/firebase/function/couponFunction';
import { referralMessages, couponMessages } from '../../../constants/messages';
import { createUserWithEmailAndPassword, signInAnonymously } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { getErrorMessageFromAuthErrorCode } from '../../../api/firebase/auth/error';
import {
  COMPANY_NAME_LABEL,
  DEPARTMENT_NAME_LABEL,
  EMAIL_LABEL,
  STAFF_NAME_LABEL,
  PHONE_LABEL,
  PASSWORD_LABEL,
  PASSWORD_CONFIRM_LABEL,
  REQUIRED_LABEL,
} from '../../../constants/labels';
import {
  COMPANY_INPUT_PLACEHOLDER,
  DEPARTMENT_INPUT_PLACEHOLDER,
  EMAIL_INPUT_PLACEHOLDER,
  NAME_INPUT_PLACEHOLDER,
  PHONE_INPUT_PLACEHOLDER,
} from '../../../constants/placeholders';

const styles = {
  cardSize: {
    '@media screen and (max-width:480px)': {
      width: 343,
    },
    boxShadow: 0,
    marginLeft: '16px',
    marginRight: '16px',
    width: 480,
  },
  cardContentPadding: {
    '&:last-child': {
      '@media screen and (max-width:480px)': {
        paddingBottom: '32px',
      },
      paddingBottom: '40px',
    },
    '@media screen and (max-width:480px)': {
      padding: '32px 16px',
    },
    padding: '40px',
  },
  required: {
    color: red50,
    marginLeft: '10px',
  },
  submitButton: {
    height: '43px',
    padding: '10px 16px',
    marginTop: '8px',
    color: primaryButtonBackgroundColor,
  },
  textMb,
  titleMb,
  referralBanner: {
    backgroundColor: blue20,
    border: `1px solid ${blue50}`,
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '16px',
    marginLeft: '16px',
    marginRight: '16px',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  referralBannerTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: blue50,
    marginBottom: '4px',
  },
  referralBannerText: {
    fontSize: '13px',
    color: gray100,
  },
  w100,
};

export interface RegisterAuthType {
  company: string;
  email?: string | null | undefined;
  name: string;
  department: string;
  password: string;
  passwordConfirm: string;
  tel: string;
  agreeToTerms: boolean;
}

// 企業招待情報の型
interface CompanyInviteInfo {
  companyId: string;
  companyName: string;
  userName: string;
  email: string;
}

/**
 * URLのクエリから企業招待による登録かを判定する（#3071）
 * トークン検証の完了を待たずに判定できるよう、URLだけで判断する
 */
const isCompanyInviteUrl = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return params.get('type') === 'company' && !!params.get('token') && !!params.get('email');
};

/** フォームの初期値。全項目を空文字で初期化し、未編集時に undefined にならないようにする */
const defaultFormValues: RegisterAuthType = {
  company: '',
  email: '',
  name: '',
  department: '',
  password: '',
  passwordConfirm: '',
  tel: '',
  agreeToTerms: false,
};

const AuthRegisterCard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [firstSubmit, setFirstSubmit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const [defaultEmail, setDefaultEmail] = useState<string>('');
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  // 企業招待かどうかはURLから初回レンダー時点で確定させる（#3071）
  // バリデーションの切り替えに使うため、トークン検証の完了を待たずに判定できる必要がある
  const [isCompanyInvite] = useState<boolean>(() => isCompanyInviteUrl(location.search));
  const [companyInviteInfo, setCompanyInviteInfo] = useState<CompanyInviteInfo | null>(null);
  const [bannerTitle, setBannerTitle] = useState<string>('');
  const [bannerText, setBannerText] = useState<string>('');
  const { user, setCurrentUser, setCurrentCompany } = useContext(AuthContext);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegisterAuthType>({
    defaultValues: defaultFormValues,
    // 企業招待では部署名・電話番号を任意にする（#3071）
    resolver: yupResolver(isCompanyInvite ? CompanyInviteRegisterSchema : AuthRegisterSchema),
  });

  const buttonDisabledFlag = (): boolean => {
    // トークンが無効な場合はボタンを無効化
    if (tokenValid === false) {
      return true;
    }

    if (firstSubmit) {
      // 企業招待では部署名・電話番号を入力必須にしない（#3071）
      const requiredFieldsUnfilled = isCompanyInvite
        ? false
        : isBlank(watch('department')) || isBlank(watch('tel'));

      return (
        isBlank(watch('company')) ||
        isBlank(watch('email')) ||
        isBlank(watch('name')) ||
        isBlank(watch('password')) ||
        isBlank(watch('passwordConfirm')) ||
        requiredFieldsUnfilled ||
        Boolean(errors.company) ||
        Boolean(errors.name) ||
        Boolean(errors.tel) ||
        Boolean(errors.password) ||
        Boolean(errors.passwordConfirm) ||
        Boolean(errors.department) ||
        (isCompanyInvite && !watch('agreeToTerms'))
      );
    }
    return false;
  };

  // トークン検証
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const emailParam = urlParams.get('email')?.split('?').join('') ?? '';
    const token = urlParams.get('token') ?? '';
    const type = urlParams.get('type') ?? '';
    const refToken = urlParams.get('ref') ?? '';

    if (emailParam) {
      setDefaultEmail(emailParam);
      setValue('email', emailParam);
    }

    // リファラルトークンの検証（バナー表示用）
    if (refToken) {
      validateReferralToken(refToken)
        .then(result => {
          if (result.valid && result.data) {
            setBannerTitle(referralMessages.bannerTitle(result.data.inviter_company_name));
            setBannerText(referralMessages.bannerText);
          }
        })
        .catch(() => {
          // トークン無効時はバナー非表示
        });
    }

    // クーポンコードの検証（バナー表示用）
    const couponParam = urlParams.get('coupon') ?? '';
    if (couponParam) {
      validateCouponCode(couponParam)
        .then(result => {
          if (result.valid && result.data) {
            const amount = result.data.reward_amount;
            const pages = Math.floor(amount / 50);
            setBannerTitle(couponMessages.bannerTitle);
            setBannerText(couponMessages.bannerText(amount, pages));
          }
        })
        .catch(() => {
          // 無効時はバナー非表示
        });
    }

    // 企業招待の場合
    if (type === 'company' && token && emailParam) {
      setLoading(true);
      const verifyCompanyInvitationToken = httpsCallable<
        { token: string; email: string },
        {
          valid: boolean;
          message: string;
          companyInfo?: CompanyInviteInfo;
        }
      >(functions, 'verifyCompanyInvitationToken');

      verifyCompanyInvitationToken({ token, email: emailParam })
        .then(result => {
          if (result.data.valid && result.data.companyInfo) {
            setTokenValid(true);
            setCompanyInviteInfo(result.data.companyInfo);
          } else {
            setTokenValid(false);
            setErrorText('招待の有効期限が切れています。運営へ再招待を依頼してください。');
            setOpen(true);
            setTimeout(() => navigate('/'), 5000);
          }
        })
        .catch(error => {
          setTokenValid(false);
          const errorMessage = error?.message || '招待の確認中にエラーが発生しました。';
          setErrorText(errorMessage);
          setOpen(true);
          setTimeout(() => navigate('/'), 5000);
        })
        .finally(() => setLoading(false));
    } else if (token && emailParam) {
      // 通常の会員登録トークンシステムの検証
      // 通常登録はメール登録時に規約同意済みのため、自動でtrueに設定
      setValue('agreeToTerms', true);
      setLoading(true);
      const verifyUserRegistrationToken = httpsCallable<
        { token: string; email: string },
        { valid: boolean; message: string; userInfo?: { email: string } }
      >(functions, 'verifyUserRegistrationToken');

      verifyUserRegistrationToken({ token, email: emailParam })
        .then(result => {
          if (result.data.valid && result.data.userInfo) {
            setTokenValid(true);
          } else {
            setTokenValid(false);
            setErrorText('登録の有効期限が切れています。会員登録画面より再度お申し込みください。');
            setOpen(true);
            setTimeout(() => navigate('/'), 5000);
          }
        })
        .catch(error => {
          setTokenValid(false);
          const errorMessage = error?.message || '登録の確認中にエラーが発生しました。';
          setErrorText(errorMessage);
          setOpen(true);
          setTimeout(() => navigate('/'), 5000);
        })
        .finally(() => setLoading(false));
    }
  }, [location, navigate, setValue]);

  // 企業招待情報がある場合、フォームにプリフィル
  useEffect(() => {
    if (companyInviteInfo) {
      setValue('company', companyInviteInfo.companyName);
      setValue('name', companyInviteInfo.userName);
    }
  }, [companyInviteInfo, setValue]);

  useEffect(() => {
    // 登録処理中でない場合のみ、ログイン状態の場合はホーム画面へ遷移
    if (user && !isRegistering) navigate('/home');
  }, [user, navigate, isRegistering]);

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      setFirstSubmit(true);
      setIsRegistering(true);

      // 匿名ログイン
      const anonymous = await signInAnonymously(auth);

      if (anonymous) {
        const createdUser = await createUserWithEmailAndPassword(
          auth,
          defaultEmail,
          watch('password'),
        );

        if (createdUser) {
          const uid = createdUser.user.uid;

          // Firestore の users/companies 操作は Functions 経由で行う（admin SDK でセキュリティルール不要）
          const completeRegistration = httpsCallable<
            | {
                type: 'normal';
                email: string;
                uid: string;
                name: string;
                companyName: string;
                department: string;
                tel: string;
              }
            // 企業招待では部署名・電話番号が未入力になり得る（#3071）
            | {
                type: 'companyInvite';
                email: string;
                uid: string;
                name: string;
                companyId: string;
                department?: string;
                tel?: string;
              },
            { success: boolean; uid: string; companyId: string; role: number }
          >(functions, 'completeRegistration');

          let result;
          if (isCompanyInvite && companyInviteInfo) {
            result = await completeRegistration({
              type: 'companyInvite',
              email: defaultEmail,
              uid,
              name: watch('name'),
              companyId: companyInviteInfo.companyId,
              department: watch('department'),
              tel: watch('tel'),
            });
          } else {
            result = await completeRegistration({
              type: 'normal',
              email: defaultEmail,
              uid,
              name: watch('name'),
              companyName: watch('company'),
              department: watch('department'),
              tel: watch('tel'),
            });
          }

          const companyId = result.data.companyId;

          // リファラルトークン or クーポンコードがあればクレジットを付与
          const urlParams = new URLSearchParams(location.search);
          const refToken = urlParams.get('ref') ?? '';
          const couponParam = urlParams.get('coupon') ?? '';
          if (refToken) {
            try {
              await redeemReferralInvitation(refToken, companyId);
            } catch {
              // 付与失敗は登録自体は成功させる
            }
          } else if (couponParam) {
            try {
              await redeemCoupon(couponParam, companyId);
            } catch {
              // 付与失敗は登録自体は成功させる
            }
          }

          // completeRegistration Function 内でカスタムクレームをセット済みのため
          // トークンを強制リフレッシュしてクレームを反映させる
          await auth.currentUser?.getIdToken(true);

          // AuthContext に設定
          setCurrentUser({
            uid,
            name: watch('name'),
            icon: null,
            role: result.data.role,
            company_id: companyId,
          });

          const companyData = await getCompany(companyId);
          if (companyData) {
            const newCompany: CompanyContextType = {
              company_id: companyId,
              company_name: companyData.company_name ?? '',
              plan: companyData.plan ?? 0,
              ocr_monthly_budget: companyData.ocr_monthly_budget ?? 0,
              ocr_credit_balance: companyData.ocr_credit_balance ?? 0,
              current_pattern_count: companyData.current_pattern_count ?? 0,
              current_user_count: companyData.current_user_count ?? 0,
              user_limit: companyData.user_limit ?? 0,
              enable_master: companyData.enable_master ?? false,
              master_self_upload: companyData.master_self_upload ?? false,
              enable_dictionary: companyData.enable_dictionary ?? false,
              enable_export_template: companyData.enable_export_template ?? false,
              pattern_limit: companyData.pattern_limit ?? 1,
            };
            setCurrentCompany(newCompany);
          }

          navigate('/auth/register/complete');
        }
      }
    } catch (error) {
      let errorMessage = '会員登録中にエラーが発生しました。';
      if (error instanceof FirebaseError) {
        if (
          error.code === 'auth/email-already-in-use' ||
          error.code === 'auth/credential-already-in-use'
        ) {
          errorMessage =
            'このメールアドレスは既に使用されています。別のメールアドレスをお試しください。';
        } else {
          errorMessage = getErrorMessageFromAuthErrorCode(error.code);
        }
      }
      setErrorText(errorMessage);
      setOpen(true);
      setIsRegistering(false);
    } finally {
      setLoading(false);
      setFirstSubmit(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <Box display="flex" flexDirection="column" alignItems="center">
          {bannerTitle && (
            <Box sx={styles.referralBanner}>
              <Typography sx={styles.referralBannerTitle}>{bannerTitle}</Typography>
              <Typography sx={styles.referralBannerText}>{bannerText}</Typography>
            </Box>
          )}
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: gray100, textAlign: 'left' }]}
                component="div"
              >
                会員情報登録
              </Typography>
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.company)}
                    helperText={errors.company?.message}
                    labelText={COMPANY_NAME_LABEL}
                    required
                    requiredText={REQUIRED_LABEL}
                    placeholder={COMPANY_INPUT_PLACEHOLDER}
                    formMb="24px"
                  />
                )}
                control={control}
                name="company"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.department)}
                    helperText={errors.department?.message}
                    labelText={DEPARTMENT_NAME_LABEL}
                    required={!isCompanyInvite}
                    requiredText={isCompanyInvite ? undefined : REQUIRED_LABEL}
                    placeholder={DEPARTMENT_INPUT_PLACEHOLDER}
                    formMb="24px"
                  />
                )}
                control={control}
                name="department"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    labelText={STAFF_NAME_LABEL}
                    required
                    requiredText={REQUIRED_LABEL}
                    placeholder={NAME_INPUT_PLACEHOLDER}
                    formMb="24px"
                  />
                )}
                control={control}
                name="name"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.tel)}
                    helperText={errors.tel?.message}
                    labelText={PHONE_LABEL}
                    required={!isCompanyInvite}
                    requiredText={isCompanyInvite ? undefined : REQUIRED_LABEL}
                    placeholder={PHONE_INPUT_PLACEHOLDER}
                    formMb="24px"
                    width="100%"
                    autoComplete="tel"
                  />
                )}
                control={control}
                name="tel"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <CustomTextfield
                    {...field}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    labelText={EMAIL_LABEL}
                    required
                    requiredText={REQUIRED_LABEL}
                    placeholder={EMAIL_INPUT_PLACEHOLDER}
                    disabled={true}
                    formMb="24px"
                    width="100%"
                  />
                )}
                control={control}
                name="email"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <PasswordTextfield
                    {...field}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    labelText={PASSWORD_LABEL}
                    requiredText={REQUIRED_LABEL}
                    required
                    formMb="24px"
                  />
                )}
                control={control}
                name="password"
              />
              <Controller
                render={({ field }): React.ReactElement => (
                  <PasswordTextfield
                    {...field}
                    error={Boolean(errors.passwordConfirm)}
                    helperText={errors.passwordConfirm?.message}
                    labelText={PASSWORD_CONFIRM_LABEL}
                    requiredText={REQUIRED_LABEL}
                    required
                    formMb="24px"
                  />
                )}
                control={control}
                name="passwordConfirm"
              />
              {isCompanyInvite && (
                <Controller
                  render={({ field }): React.ReactElement => (
                    <Box
                      sx={{
                        marginBottom: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value || false}
                            onChange={(e): void => {
                              if (tokenValid === false) return;
                              field.onChange(e.target.checked);
                            }}
                            disabled={tokenValid === false}
                            sx={{
                              '&.Mui-checked': {
                                color: orange50,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" component="span">
                            <Link
                              href={CORPORATE_TERMS_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textDecoration: 'underline', color: gray100 }}
                            >
                              利用規約
                            </Link>
                            と
                            <Link
                              href={CORPORATE_PRIVACY_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textDecoration: 'underline', color: gray100 }}
                            >
                              プライバシーポリシー
                            </Link>
                            に同意する
                          </Typography>
                        }
                      />
                      {errors.agreeToTerms && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            marginLeft: '32px',
                            marginTop: '-8px',
                            color: red50,
                            fontSize: '12px',
                          }}
                        >
                          {errors.agreeToTerms?.message}
                        </Typography>
                      )}
                    </Box>
                  )}
                  control={control}
                  name="agreeToTerms"
                />
              )}

              <Button
                disabled={buttonDisabledFlag()}
                variant="contained"
                fullWidth
                sx={[styles.submitButton]}
                onClick={(): void => {
                  void (async (): Promise<void> => {
                    setFirstSubmit(true);
                    await handleSubmit(async () => {
                      await onSubmit();
                    })();
                  })();
                }}
              >
                <Typography component="div" color="white" sx={{ fontSize: 16, fontWeight: 700 }}>
                  登録する
                </Typography>
              </Button>
            </CardContent>
          </Card>
          <CustomSnackbar open={open} onClose={(): void => setOpen(false)} text={errorText} />
        </Box>
      </Box>
    </>
  );
};

export default AuthRegisterCard;
